/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import { Channel } from '../../../core/entities/Channel';
import { VodInteractor } from '../../../core/interactors';
import { Log } from '../../../core/logging/Log';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import type { TGetState, TivoPayloadAction } from '../../../core/tivo/tivo-redux-wrapper';
import { TChannelsV2, TChannelV2 } from '../../../core/types';
import { TChannels } from '../../../core/types/TChannels';
import { isJumpChannelCcbSwitchEnabled } from '../../../core/utils/ccb/CcbSwitchHelper';
import { showTemporaryGuideIssueOverlay } from '../../overlay/temporaryguideissueoverlay/temporaryguideissueoverlay.reducers';
import { setScreenTaskController } from '../../screens/screens.reducers';
import {
    loadGridGuideChannelsAction,
    resetGridGuideRowsAction,
    setGridGuideOffsetAction,
    setIsChannelsServiceFailAction,
} from '../guide.actions';
import { loadGridGuideChannels } from '../guide.reducers';
import { setVerticalPositionAction } from '../guidehighlight/guidehighlight.actions';
import {
    startGuidePageScrollAction,
    startTurboScrollAction,
    stopGuidePageScrollAction,
    stopTurboScrollAction,
} from './turboscroll.actions';
import {
    START_GUIDE_PAGE_SCROLL,
    START_TURBO_SCROLL,
    STOP_GUIDE_PAGE_SCROLL,
    STOP_TURBO_SCROLL,
} from './turboscroll.types';

const TAG = 'TurboScroll';
const initialState: {
    isTurboScrollActive: boolean;
    isUpward: boolean;
    isGuidePageScrollActive: boolean;
    isGuidePageUpward: boolean;
} = {
    isTurboScrollActive: false,
    isUpward: false,
    isGuidePageScrollActive: false,
    isGuidePageUpward: false,
};

export const turboScrollReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<typeof initialState>>{},
) => {
    switch (type) {
        case START_TURBO_SCROLL:
        case STOP_TURBO_SCROLL:
            return {
                ...state,
                isTurboScrollActive: payload.isTurboScrollActive,
                isUpward: payload.isUpward,
            };
        case START_GUIDE_PAGE_SCROLL:
        case STOP_GUIDE_PAGE_SCROLL:
            return {
                ...state,
                isGuidePageScrollActive: payload.isGuidePageScrollActive,
                isGuidePageUpward: payload.isGuidePageUpward,
            };
        default:
            return state;
    }
};

export const fetchAndUpdateChannels = () => (dispatch: Function, getState: TGetState) => {
    const { isUpward, isGuidePageUpward, isGuidePageScrollActive } = getState().turboScroll;
    const { offset, channelsCount, LIMIT } = getState().gridGuide;
    const { channelsType } = getState().channelOptionsOverlay;
    const scrollUpward = isUpward || isGuidePageUpward;
    const limit = isGuidePageScrollActive ? LIMIT : 1;
    const currentOffset = scrollUpward
        ? (offset - limit + channelsCount) % channelsCount
        : (offset + limit) % channelsCount;

    dispatch(resetGridGuideRowsAction());
    const services: VodInteractor = new VodInteractor();
    const fetchChannels = isJumpChannelCcbSwitchEnabled()
        ? services.fetchChannelsV2
        : services.fetchChannels;
    fetchChannels(channelsType, currentOffset, LIMIT)
        .then((response: TChannels | TChannelsV2) => {
            const stationIds = <string[]>response.channels.map((channel: Channel | TChannelV2) => {
                return channel.stationId;
            });
            dispatch(
                loadGridGuideChannelsAction(
                    response.channels,
                    response.channelCount,
                    stationIds,
                    false,
                ),
            );
        })
        .catch((error) => {
            dispatch(setIsChannelsServiceFailAction(true));
            dispatch(showTemporaryGuideIssueOverlay());
            Diagnostics.error(
                TAG,
                'Failed to get channels.fetchAndUpdateChannels -> services.fetchChannels.',
                error,
            );
            Log.e(TAG, error);
        });

    dispatch(setGridGuideOffsetAction(currentOffset));
};

export const startTurboScroll =
    (isUpward: boolean = false) =>
    (dispatch: Function, getState: TGetState) => {
        const { screenTaskController } = getState().screens;
        if (screenTaskController !== ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS) return;
        Diagnostics.info(TAG, 'TurboScroll enabled');
        Log.i(TAG, 'TurboScroll enabled');
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.TURBO_SCROLL));
        dispatch(startTurboScrollAction(isUpward));
    };

export const stopTurboScroll = () => (dispatch: Function, getState: TGetState) => {
    const { gridGuide, screens, turboScroll } = getState();
    const { LIMIT } = gridGuide;
    const { screenTaskController } = screens;
    const { isUpward } = turboScroll;
    if (screenTaskController !== ScreenTaskControllerTypes.TURBO_SCROLL) return;
    Diagnostics.info(TAG, 'TurboScroll disabled');
    Log.i(TAG, 'TurboScroll disabled');
    dispatch(stopTurboScrollAction());
    dispatch(loadGridGuideChannels());
    dispatch(setVerticalPositionAction(isUpward ? 0 : LIMIT - 1));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS));
};

export const startGuidePageScroll =
    (isGuidePageUpward: boolean = false) =>
    (dispatch: Function, getState: TGetState) => {
        const { screenTaskController } = getState().screens;
        if (screenTaskController !== ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS) return;
        Diagnostics.info(TAG, 'PageScroll enabled');
        Log.i(TAG, 'PageScroll enabled');
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.TURBO_SCROLL));
        dispatch(startGuidePageScrollAction(isGuidePageUpward));
    };

export const stopGuidePageScroll = () => (dispatch: Function, getState: TGetState) => {
    const { gridGuide, screens, turboScroll } = getState();
    const { LIMIT } = gridGuide;
    const { screenTaskController } = screens;
    const { isGuidePageUpward } = turboScroll;
    if (screenTaskController !== ScreenTaskControllerTypes.TURBO_SCROLL) return;
    Diagnostics.info(TAG, 'PageScroll disabled');
    Log.i(TAG, 'PageScroll disabled');
    dispatch(stopGuidePageScrollAction());
    dispatch(loadGridGuideChannels());
    dispatch(setVerticalPositionAction(isGuidePageUpward ? 0 : LIMIT - 1));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS));
};

export default turboScrollReducer;
